import React from "react";
import {useTrackingGroup, getActiveInteractionId} from "../context/TrackingContext";

import {EVENT_NAMES} from "tracking/core/eventNames";
import {
    dataLayerFormBegin,
    dataLayerFormSubmit,
    dataLayerFormSubmitError, dataLayerNewsletterSubscribe
} from "tracking/events/forms";

export type UseFormTrackingParams = {
    formId: string | number | null;
    formName: string | null;
    formType?: string | null;
};

export function useFormTracking(params: UseFormTrackingParams) {
    const {startInteraction, endInteraction} = useTrackingGroup();
    const startedRef = React.useRef(false);

    const getOptions = React.useCallback(() => {
        const id = getActiveInteractionId();
        return id ? {eventGroupId: id} : undefined;
    }, []);

    const ensureBegin = React.useCallback(() => {
        if (startedRef.current) return;
        startedRef.current = true;

        dataLayerFormBegin(
            params.formId,
            params.formName,
            params.formType,
            getOptions()
        );
    }, [params.formId, params.formName, params.formType, getOptions]);

    const onFieldInteract = React.useCallback(() => {
        ensureBegin();
    }, [ensureBegin]);

    const onSubmitClick = React.useCallback(() => {
        startInteraction(EVENT_NAMES.FORM_SUBMIT);
        ensureBegin();
    }, [startInteraction, ensureBegin]);

    const onSubmitSuccess = React.useCallback(() => {
        ensureBegin();
        dataLayerFormSubmit(
            params.formId,
            params.formName,
            params.formType,
            undefined
        );
        endInteraction();

    }, [params.formId, params.formName, params.formType, ensureBegin, endInteraction]);

    const onSubmitError = React.useCallback(
        (errorMessage?: string | null) => {
            ensureBegin();

            dataLayerFormSubmitError(
                params.formId,
                params.formName,
                params.formType,
                errorMessage ?? null,
                getOptions()
            );
        },
        [params.formId, params.formName, params.formType, ensureBegin, getOptions]
    );

    const fireNewsletterSubscribe = React.useCallback(() => {
        dataLayerNewsletterSubscribe(undefined);
    }, []);

    return {
        onFieldInteract,
        onSubmitClick,
        onSubmitSuccess,
        onSubmitError,
        endInteraction,
        fireNewsletterSubscribe,
    } as const;
}